import { useState } from 'react'
import PropTypes from 'prop-types'
import styles from './styles/Reference.css'
import EditorialBadges from '../misc/EditorialBadges'
import Link from 'components/misc/Link'
import Badge from '../misc/Badge'
import Tooltip from '../misc/Tooltip'
import classNames from 'classnames'
import copy from 'copy-to-clipboard'
import LinkResolverLink from './LinkResolverLink'
import isPreprint from '../../lib/is-preprint'
import fullTextLink from '../../lib/fulltext-link'
import { decodeHTMLEntities } from 'lib/util'

const Badges = ({ doi, type, notices, selfCites }) => (
  <>
    <EditorialBadges key='badges' notices={notices} />
    {isPreprint(type, doi) && (
      <Badge className={styles.preprintBadge}>
        Preprint
      </Badge>
    )}
    {selfCites?.length > 0 && (
      <Badge className={styles.preprintBadge}>
        <Tooltip content={selfCites.reduce((acc, next, i) => acc + `${next.family}, ${next.given}${i === selfCites.length - 1 ? '' : ', '}`, '')}>
          Self Cite
        </Tooltip>
      </Badge>
    )}
  </>
)

export const Reference = ({
  journal,
  journalSlug,
  journalAsBadge,
  shortJournal,
  issue,
  volume,
  page,
  section,
  year,
  pmPublicationType,
  editorialNotices,
  doi,
  issns,
  memberId,
  showDOI,
  type,
  selfCites,
  showSciteReport,
  handleFulltextClick,
  handleGenerateCitation
}) => {
  const [copied, setCopied] = useState(false)
  let journalUrl
  if (journalSlug) {
    journalUrl = `/journals/${journalSlug}`
  } else if (issns && issns?.length > 0) {
    const strippedIssn = issns[0]?.replace(/<[^>]*>?/gm, '') // incase the issn has highlighting from the api
    journalUrl = `/journals/${strippedIssn}`
  }

  const handleCopyDOI = () => {
    copy(doi)
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  const decodedJournalName = decodeHTMLEntities(shortJournal || journal)
  return (
    <span className={styles.reference}>
      <span>
        {(decodedJournalName) && (

          journalAsBadge
            ? (
              <Badge className={styles.journalBadge} tooltipCopy={journalUrl && `View ${decodedJournalName} journal dashboard.`}>
                {journalUrl ? <Link to={journalUrl}>{decodedJournalName}</Link> : { decodedJournalName }}
              </Badge>
              )
            : (
              <Link to={journalUrl} underline key='journal'><em dangerouslySetInnerHTML={{ __html: decodedJournalName }} /></Link>
              )
        )}
        {section && <span className={styles.section}> Section: <span dangerouslySetInnerHTML={{ __html: section }} /></span>}
        {year && <span className={styles.pubYear}> {year} </span>}
        {pmPublicationType === 'Review' && '. Review'}
        {showDOI && doi && (
          <div className={styles.doiContainer}>
            <span>DOI: {doi}</span>
            <Tooltip
              placement='bottom'
              content='Copy DOI'
              refClassName={styles.tooltip}
            >
              <i
                className={classNames('copy', {
                  'fa fa-copy fa-lg': !copied,
                  'fa fa-check-circle fa-lg green': copied
                })} onClick={handleCopyDOI}
              />
            </Tooltip>
            <span> |</span>
            <Link className={styles.fullTextLink} onClick={handleFulltextClick} underline blue href={fullTextLink({ doi, memberId })} target='_blank' rel='noreferrer nofollow'> View full text </Link>

            {handleGenerateCitation && (
              <>
                <span> |</span>
                <Link className={styles.fullTextLink} blue underline onClick={handleGenerateCitation}>
                  Cite
                </Link>
              </>)}
            {editorialNotices?.length > 0 && (<span>|</span>)}
            <LinkResolverLink doi={doi} issns={issns} memberId={memberId} handleFulltextClick={handleFulltextClick} referenceStyle />
          </div>
        )}
        {showSciteReport && doi && (
          <Link className={styles.fullTextLink} underline blue href={`https://scite.ai/reports/${doi}`} target='_blank' rel='noreferrer'> Read how this publication is cited </Link>
        )}
      </span>
      <Badges
        key='notice-badges'
        notices={editorialNotices}
        type={type}
        selfCites={selfCites}
        doi={doi}
      />
    </span>
  )
}

Reference.propTypes = {
  journal: PropTypes.string,
  shortJournal: PropTypes.string,
  issue: PropTypes.string,
  volume: PropTypes.string,
  page: PropTypes.string,
  section: PropTypes.string,
  year: PropTypes.number,
  pmPublicationType: PropTypes.string,
  doi: PropTypes.string,
  selfCites: PropTypes.array
}

export default (Reference)
